import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  NgxQrcodeElementTypes,
  NgxQrcodeErrorCorrectionLevels,
} from '@techiediaries/ngx-qrcode';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { IvSignService, MetadataOperation } from '../../../api';
import { UserService } from '../../../api/api/user.service';
import { RequestBiometry } from '../../../api/model/requestBiometry';
import { BiometrySignatureRequest } from '../../../api/model/biometrySignatureRequest';
import { ProfileService } from '../../../core/profile/profile.service';
import { loadTextFileAjaxSync } from '../../../core/utils/load-ajax-basic';
import { TranslateService } from './../../../core/translation';
import { MessageService } from './../../../shared/message/message.service';
import { Environment } from '../../../models/environment';
import { PersonalizationsService } from '../../../base/services/personalizations/personalizations.service';
import { IvPadComponent } from '../../../shared/components/iv-pad/iv-pad.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-simplesigner',
  templateUrl: './simplesigner.component.html',
  styleUrls: ['./simplesigner.component.scss'],
  providers: [IvSignService, UserService],
})
export class SimpleSignerComponent implements AfterViewInit, OnDestroy {
  /**
   * IvPad component from ivpad librarie.
   */
  @ViewChild('ivpad') ivpad: IvPadComponent;
  /**
   * Event triggered after finish the signature.
   */
  @Output() afterSigned: EventEmitter<any[]> = new EventEmitter();

  /**
   * Event triggered when there's an error.
   */
  @Output() onError = new EventEmitter();

  /**
   * Boolean used for the signature checkbox.
   */
  checked = false;

  /**
   * Boolean that controls if the pre-saved signature can be used.
   */
  saveSignatureUser = false;

  /**
   * Boolean that controls if is a biometry signature.
   */
  biometry = false;

  /**
   * String containing the IvSign token
   */
  token: string = null;

  /**
   * Boolean containing the sign
   */
  containsSign: boolean = true;

  /**
   * Array containing the signed documents.
   */
  signed: any[] = null;

  /**
   * Array that contains the pdfs of the circuit.
   */
  pdfs: any[] = null;

  /**
   * Object for the sign widget
   */
  widget: any;

  /**
   * Boolean for set if the circuit has an afterview event
   */
  afterView = false;

  /**
   * Obteins device
   */
  elementType = NgxQrcodeElementTypes.URL;
  correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;

  /**
   * Variable that contains the url
   */
  routeQR: string = '';

  /**
   * Array for the pdf form fields data
   */
  metadataDocument: Array<MetadataOperation> = [];

  /**
   * Obteins device
   */
  mobile: boolean;
  /**
   * Verify if setting of pressure is active
   */
  verifyPressure: number;

  /**
   * Verify if device if allowed for biometric sign
   */
  deviceAllowed: boolean;

  /**
   * String for the operation ID
   */
  operationId: string;

  environment: Environment;

  indexBio: number = 0;

  initPerm: boolean = true;

  signedPDF: EventEmitter<number> = new EventEmitter<number>();

  indexPDF: number = 0;

  repeatIndex: number = 0;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public IvSignService: IvSignService,
    private _translate: TranslateService,
    private _profile: ProfileService,
    private _ivSign: IvSignService,
    private _userService: UserService,
    public _message: MessageService,
    private readonly ref: ChangeDetectorRef,
    public _router: Router,
    public _dialog: MatDialog,
    private _personalization: PersonalizationsService
  ) {
    this.environment = this._personalization.sendEnv;
  }

  async ngAfterViewInit() {
    const userAgent = navigator.userAgent.toLowerCase();
    const userTouch = navigator.maxTouchPoints;
    const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent);
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(
        navigator.userAgent
      ) || isTablet
    ) {
      this.mobile = true;
    } else {
      userTouch !== 0 ? this.mobile = true : this.mobile = false;
    }
    this.ivpad.changeConfig({ autoDownload: false, showButtons: false });
    this.ivpad.disableErrors();
    this.ivpad.disableLoading();
    this.ivpad.signatureCanvas.setOptions('backgroundColor', 'rgba(0,0,0,0)');
    this.afterView = true;
    if (this.biometry) {
      this.setBiometry(true);
    }
    if (
      this.environment?.features.saveSignatureUser &&
      this._profile.getUser() &&
      !this.biometry
    ) {
      this.saveSignatureUser = true;
      this.setPreSavedSignature();
    }
    this.ref.detectChanges();
  }

  ngOnDestroy() {
    this.destroy$.unsubscribe();
   }

  /**
   * Function that sets the configuration of the biometry signature.
   * @param {boolean} biometry Boolean that controls if is a biometry signature.
   */
  setBiometry(biometry: boolean) {
    this.biometry = biometry;
    if (this.afterView) {
      this.ivpad?.setBiometry(biometry);
      if (biometry) {
        this.getToken(this.token);
        this.ivpad?.setPublicKey(this.getPublicKey());
      }
    }
    this.ref.detectChanges();
  }

  /**
   * The function sets the data of the operation for sign
   * @param {any} data The data of the operation
   */
  setData(data: any) {
    this.verifyPressure = data.verifyPressure;
    this.pdfs = data.pdfs;
    this.widget = data.widget;
    this.token = data.token;
    this.deviceAllowed = data.deviceAllowed;
    this.operationId = data.id;
    this.ref.detectChanges();
  }

  /**
   * The function gets the signature maded by the user
   */
  getSignature(): any[] {
    const sig = this.ivpad.signatureCanvas
      .toDataURL('image/png')
      .replace('data:image/png;base64,', '');
    if (this.ivpad.checkSignature()) {
      this.containsSign = false;
      this._message.showError(
        this._translate.instant('otpsigner_error_no_signature')
      );
      return;
    }
    if (this.checked) {
      return [
        { key: 'firma_imagen', value: sig },
        { key: 'saveNewSignature', value: '1' },
      ];
    } else {
      return [{ key: 'firma_imagen', value: sig }];
    }
  }

  /**
   * Function that reset the signature window.
   */
  clear() {
    this.ivpad?.clearSign();
  }

  /**
  * Function that sets the signature in the pdf.
  */
  signPDFs() {
    this.signed = [];
    if (this.widget.length && this.widget) {
      let indexWidget = 0;
      for (const widg of this.widget) {
        const index = this.pdfs.findIndex((res) => res.idDocument === widg.doc);

        if (this.pdfs[index]) {
          this.pdfs[index]['widget'] = true;
          this.pdfs[index]['indexWidget'] = indexWidget;
        }
        indexWidget++;
      }
      this.subscribePDF();

      this.pdfs.forEach((pdf, index) => {
        if (pdf.widget) {
          const widget = this.widget[pdf.indexWidget];
          const d = JSON.parse(widget.design);
          this.setWidget({
            x: Math.round(widget.positionX) || 0,
            y: Math.round(widget.positionY) || 0,
            page: widget.page || 1,
            w: Math.round(d.height) || 84,
            h: Math.round(d.height) || 84,
          });

        }
        else {
          if (this.widget) {
            this.setWidget({
              x: 0,
              y: 0,
              page: 1,
              w: 84,
              h: 84,
            });
          }
        }


        if (this.initPerm) {
          this.ivpad?.setPdf(pdf.b64);
          this.ivpad?.signPDF(false, true);
          this.signed = [];
        }

        index++;
      })
    }
    else {
      if (this.widget) {
        this.subscribePDF();
        this.setWidget({
          x: 0,
          y: 0,
          page: 1,
          w: 84,
          h: 84,
        });
        this.signed = [];
        this.ivpad?.setPdf(this.pdfs[0].b64);
        this.ivpad?.signPDF(false, true);
      }
    }
  }


  subscribePDF(): void {
    this.signedPDF.subscribe(response => {
      this.ivpad?.setPdf(this.pdfs[response].b64);
      this.ivpad?.signPDF(false, true);
    })
  }


  /**
   * Signs the PDF using the endPoint for biometric signature
   * @param {RequestBiometry} request Request object to be sent to the API
   */
  signPDF(request: BiometrySignatureRequest) {
    this.metadataDocument = [];
    for (let i = 0; i < this.pdfs.length; i++) {
      if (request.document === this.pdfs[i].b64) {
        this.metadataDocument.push({
          key: 'idDocumentToSign',
          value: this.pdfs[i].idDocument,
        });
      }
    }
    this.metadataDocument.push({
      key: 'idOperationToSign',
      value: this.operationId,
    });
    for (let i = 0; i < this.metadataDocument.length; i++) {
      this.metadataDocument[i].key = this.metadataDocument[i].key.replace('___', '.');
    }
    const finalRequest: RequestBiometry = {
      request: request,
      metadataDocument: this.metadataDocument,
    };
    this.initPerm = false;
    this.indexPDF++;
    this._ivSign
      .ivSignBiometrySignature(finalRequest, this._profile.getToken() || '1')
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.submit(response.data, null, this.indexPDF);
          if (this.indexPDF < this.pdfs.length) {
            this.signedPDF.emit(this.indexPDF);
          }
          this.metadataDocument = [];
          // @ts-ignore EL BEAN DEL SWAGGER NO CORRESPONDE A LA RESPUESTA
        },
        (error) => { }
      );
  }

  /**
   * The function submits the sign form
   * @param {string} event the event fired on submit
   * @param {image} image Indicates if the event is an image or pdf (in b64)
   */
  submit(event: string, image?: boolean, index?: number) {
    if (image) {
      this.afterSigned.emit([{ key: 'firma_imagen', value: event }]);
    } else if (this.pdfs) {
      this.signed.push({
        idDocument:
          this.pdfs[index - 1].idDocument,
        b64: event,
      });
      if (this.signed.length == this.pdfs.length) {
        this.afterSigned.emit(this.signed);
      } else {
        this.ivpad.setPdf(this.pdfs[this.signed.length].b64);
        for (let i = 0; i < this.widget.length; i++) {
          if (this.widget[i].doc == this.pdfs[this.signed.length].idDocument) {
            const d = JSON.parse(this.widget[i].design);
            this.setWidget({
              x: Math.round(this.widget[i].positionX) || 0,
              y: Math.round(this.widget[i].positionY) || 0,
              page: this.widget[i].page || 1,
              w: Math.round(d.height) || 84,
              h: Math.round(d.height) || 84,
            });
          }
        }
        //this.ivpad.signPDF(false, this.signed.length + 1 !== this.pdfs.length);
      }
    }
  }

  /**
   * The function obtains the cert's public key for sign
   */
  private getPublicKey(): string {
    const key = this.loadJSON('assets/cer/cer.json').key;
    return key;
  }

  /**
   * The function emits the error to the user
   * @param {any} error The error produced
   */
  emitError(error: any) {
    this.onError.emit(error);
  }

  /**
   * Function that sets the sign config when is a biometry signature
   * @param points value that contains the actual sign config with points
   */
  setConfig(points) {
    this.ivpad.signatureCanvas.fromData(JSON.parse(points));
  }

  /**
   * Function that sets IvSign token in IvPad component.
   * @param {string} token String containing the IvSign token.
   */
  private getToken(token?: string): void {
    this.IvSignService.ivSignGetSigBio(token || '0')
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.ivpad?.setToken(response.data);
        },
        (error) => {
          this.emitError(error);
        }
      );
  }

  /**
   * Function that sets data of widget in IvPad component.
   * @param {any} data Object that contains data of the widget.
   */
  setWidget(data: any) {
    this.ivpad?.setSealPosition(data.x, data.y, data.page);
    this.ivpad?.setSealSize(data.w, data.h);
  }

  /**
   * Function that obtains a json and returns an object from it.
   * @param {String} filePath String that contains the filepath.
   */
  loadJSON(filePath: string) {
    const json = loadTextFileAjaxSync(filePath, 'application/json');
    return JSON.parse(json);
  }

  /**
   * Function that obtains the saved signature of the user.
   */
  setPreSavedSignature() {
    if (this._profile.getUser()) {
      const username = this._profile.getUser();
      this._userService
        .userGetSignature(username.username, this._profile.getToken())
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response) => {
            if (response) {
              const sign = response.data;
              if (sign) {
                this.ivpad?.signatureCanvas.fromDataURL(
                  'data:image/png;base64,' + sign
                );
              }

              return;
            }
          },
          (error) => {
            this.emitError(error);
          }
        );
    }
  }
}
